import React, { useMemo, useState } from 'react';
import { SunIcon, MoonIcon } from 'lucide-react';
import { MOCK_LOANS, LOAN_STAGES, AlertType, LoanStage } from '../data/mockData';
import { PipelineFilters } from './PipelineFilters';
import { StageSection } from './StageSection';
export const LoanPipeline: React.FC = () => {
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedAlerts, setSelectedAlerts] = useState<AlertType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [darkMode, setDarkMode] = useState<boolean>(false);
  // Initialize all stages as expanded
  const [expandedStages, setExpandedStages] = useState<Record<LoanStage, boolean>>(() => {
    const initial: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      initial[stage] = true;
    });
    return initial as Record<LoanStage, boolean>;
  });
  const handleClearFilters = () => {
    setSelectedDivision('');
    setSelectedTeam('');
    setSelectedAlerts([]);
    setSearchQuery('');
  };
  const toggleStage = (stage: LoanStage) => {
    setExpandedStages(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };
  const expandAll = () => {
    const allExpanded: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allExpanded[stage] = true;
    });
    setExpandedStages(allExpanded as Record<LoanStage, boolean>);
  };
  const collapseAll = () => {
    const allCollapsed: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allCollapsed[stage] = false;
    });
    setExpandedStages(allCollapsed as Record<LoanStage, boolean>);
  };
  const filteredLoans = useMemo(() => {
    return MOCK_LOANS.filter(loan => {
      // Filter by search query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = loan.borrowerName.toLowerCase().includes(q) || loan.loanNumber.toLowerCase().includes(q) || loan.loanOfficer.toLowerCase().includes(q) || loan.propertyAddress.toLowerCase().includes(q) || loan.teamLead.toLowerCase().includes(q) || loan.divisionLead.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      // Filter by Division Lead
      if (selectedDivision && loan.divisionLead !== selectedDivision) {
        return false;
      }
      // Filter by Team Lead
      if (selectedTeam && loan.teamLead !== selectedTeam) {
        return false;
      }
      // Filter by Alerts (must have at least one of the selected alerts if any are selected)
      if (selectedAlerts.length > 0) {
        const hasMatchingAlert = loan.alerts.some(alert => selectedAlerts.includes(alert));
        if (!hasMatchingAlert) {
          return false;
        }
      }
      return true;
    });
  }, [selectedDivision, selectedTeam, selectedAlerts, searchQuery]);
  const allExpanded = Object.values(expandedStages).every(Boolean);
  return <div className={`flex flex-col h-screen overflow-hidden font-sans ${darkMode ? 'dark bg-gray-900' : 'bg-gray-100'}`} data-id="element-24">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-none transition-colors duration-200" data-id="element-25">
        <div className="flex items-center gap-6" data-id="element-26">
          <img src="https://cdn.magicpatterns.com/uploads/mrGufM5MFoDnTo49ELcw9x/2025-10-31_EZLoans_LogoAssets-HorizontalBlack_resized.png" alt="EZ Loans" className="h-8 object-contain block dark:hidden" data-id="element-27" />
          <img src="https://cdn.magicpatterns.com/uploads/jRVMd8MT9eXArUDKWYuZaB/2025-10-31_EZLoans_LogoAssets-HorizontalWhite_resized.png" alt="EZ Loans" className="h-8 object-contain hidden dark:block" data-id="element-28" />
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 hidden sm:block transition-colors duration-200" data-id="element-29"></div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 hidden sm:block transition-colors duration-200" data-id="element-30">
            Loan Pipeline
          </h1>
        </div>
        <div className="flex items-center gap-4" data-id="element-31">
          {/* Dark Mode Toggle */}
          <button onClick={() => setDarkMode(!darkMode)} className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} aria-label="Toggle dark mode" data-id="element-32">
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-sm transition-transform duration-300 flex items-center justify-center ${darkMode ? 'translate-x-9' : 'translate-x-1'}`} data-id="element-33">
              {darkMode ? <MoonIcon className="h-4 w-4 text-[#6DB33F]" data-id="element-34" /> : <SunIcon className="h-4 w-4 text-[#6DB33F]" data-id="element-35" />}
            </span>
          </button>

          <div className="w-8 h-8 rounded-full bg-[#6DB33F] text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-36">
            JD
          </div>
        </div>
      </header>

      {/* Filters */}
      <PipelineFilters selectedDivision={selectedDivision} setSelectedDivision={setSelectedDivision} selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam} selectedAlerts={selectedAlerts} setSelectedAlerts={setSelectedAlerts} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onClearFilters={handleClearFilters} totalLoans={filteredLoans.length} data-id="element-37" />

      {/* Pipeline Board (List View) */}
      <main className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-38">
        <div className="w-full" data-id="element-39">
          {/* Summary Bar */}
          <div className="flex items-center justify-between mb-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-5 py-3 transition-colors duration-200" data-id="element-40">
            <div className="flex items-center gap-2" data-id="element-41">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-200" data-id="element-42">
                Total Pipeline Value
              </span>
              <span className="text-lg font-bold text-[#6DB33F]" data-id="element-43">
                {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
              }).format(filteredLoans.reduce((sum, loan) => sum + loan.amount, 0))}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500 ml-1 transition-colors duration-200" data-id="element-44">
                ({filteredLoans.length} loan
                {filteredLoans.length !== 1 ? 's' : ''})
              </span>
            </div>
            <button onClick={allExpanded ? collapseAll : expandAll} className="text-sm font-medium text-[#6DB33F] hover:text-green-700 transition-colors" data-id="element-45">
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Stage Sections */}
          <div className="flex flex-col gap-2" data-id="element-46">
            {LOAN_STAGES.map(stage => {
            const stageLoans = filteredLoans.filter(loan => loan.stage === stage);
            return <StageSection key={stage} stage={stage} loans={stageLoans} isExpanded={expandedStages[stage]} onToggle={() => toggleStage(stage)} data-id="element-47" />;
          })}
          </div>
        </div>
      </main>
    </div>;
};